import React from 'react';
import { View, Text, TextInput, Pressable, Platform, type ImageSourcePropType } from 'react-native';
import { Image } from 'expo-image';
import { Globe, Pencil } from 'lucide-react-native';
import { FriendAvatar } from '../friends/shared/FriendAvatar';
import { WEB_HOVER_TRANSITION_STYLE } from '../../src/shared/constants/animations';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../src/shared/helpers/imageWebAlt.helper';
import {
  DISPLAY_NAME_MAX_LENGTH,
  GLOBAL_MENU_COPY,
  GLOBAL_MENU_GUEST_UPGRADE,
  GLOBAL_MENU_IMPERSONATION,
} from './globalMenuModal.constants';
import { MENU_AVATAR_SIZE } from './GlobalMenuModal.styles';
import type { getGlobalMenuModalStyles } from './GlobalMenuModal.styles';
import type { Theme } from '../../src/theme/getTheme';
import type { DisplayAvatarId } from '../../src/avatars/types/avatar.types';
import type { EquippedMap } from '../avatars/LayeredAvatarLab/characterCustomizer.types';

export interface MenuProfileSectionProps {
  styles: ReturnType<typeof getGlobalMenuModalStyles>;
  theme: Theme;
  showWebPressableTransition: boolean;
  disableAnimations: boolean;
  testID?: string;
  currentAvatarId: DisplayAvatarId | null | undefined;
  currentAvatarConfig: EquippedMap | null | undefined;
  userImageUrl: string | null | undefined;
  displayName: string;
  usernameHandle: string;
  onAvatarPress: () => void;
  isEditingDisplayName: boolean;
  draftFullName: string;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onChangeText: (text: string) => void;
  onSave: () => void;
  coins: number;
  onOpenDailyReward: () => void;
  coinIconSource: ImageSourcePropType;
  isImpersonatingSession: boolean;
  impersonationTargetLabel: string | null;
  onExitImpersonation: () => void;
  isGuest: boolean;
  onUpgradeAccount: () => void;
}

export const MenuProfileSection: React.FC<MenuProfileSectionProps> = ({
  styles,
  theme,
  showWebPressableTransition,
  disableAnimations: _disableAnimations,
  testID,
  currentAvatarId,
  currentAvatarConfig,
  userImageUrl,
  displayName,
  usernameHandle,
  onAvatarPress,
  isEditingDisplayName,
  draftFullName,
  onStartEdit,
  onCancelEdit,
  onChangeText,
  onSave,
  coins,
  onOpenDailyReward,
  coinIconSource,
  isImpersonatingSession,
  impersonationTargetLabel,
  onExitImpersonation,
  isGuest,
  onUpgradeAccount,
}) => {
  const iconColor = theme.gray?.[600] ?? theme.gray?.[700] ?? '#71717a';

  return (
    <View style={styles.profileSection}>
      <View style={styles.topBar}>
        <View style={styles.themeToggleWrapper} />
        <Pressable
          style={({ pressed, hovered }) => [
            styles.languageButton,
            showWebPressableTransition && Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.languageButtonPressed,
            hovered && Platform.OS === 'web' && styles.languageButtonHover,
          ]}
          testID={testID ? `${testID}-language` : 'global-menu-modal-language'}
          accessibilityLabel="Language: English"
        >
          <Globe size={20} color={iconColor} style={styles.languageIcon} />
          <Text style={styles.languageCode}>EN</Text>
        </Pressable>
      </View>

      <View style={styles.profileContent}>
        <Pressable
          style={({ pressed, hovered }) => [
            styles.avatarWrapper,
            showWebPressableTransition && Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.avatarWrapperPressed,
            hovered && Platform.OS === 'web' && styles.avatarWrapperHover,
          ]}
          onPress={onAvatarPress}
          accessibilityLabel="Change avatar"
          testID={testID ? `${testID}-avatar` : 'global-menu-modal-avatar'}
        >
          <FriendAvatar
            avatarId={currentAvatarId ?? undefined}
            avatarConfig={currentAvatarConfig ?? undefined}
            fallbackUrl={userImageUrl ?? undefined}
            nameForInitials={displayName}
            size={MENU_AVATAR_SIZE}
            testID={testID ? `${testID}-avatar` : 'global-menu-modal-avatar'}
          />
        </Pressable>

        <View style={styles.displayNameRow}>
          {isEditingDisplayName ? (
            <View style={styles.displayNameEditContainer}>
              <TextInput
                style={styles.nameInput}
                value={draftFullName}
                onChangeText={onChangeText}
                placeholder={GLOBAL_MENU_COPY.FULL_NAME_LABEL}
                placeholderTextColor={theme.gray?.[500] ?? undefined}
                maxLength={DISPLAY_NAME_MAX_LENGTH}
                autoFocus
                testID={testID ? `${testID}-display-name-input` : 'global-menu-modal-display-name-input'}
              />
              <Text style={styles.displayNameHint}>
                {GLOBAL_MENU_COPY.DISPLAY_NAME_HINT}
                {draftFullName.length > 0 ? ` (${draftFullName.trim().length}/${DISPLAY_NAME_MAX_LENGTH})` : ''}
              </Text>
              <View style={styles.saveCancelRow}>
                <Pressable
                  style={({ pressed, hovered }) => [
                    styles.saveButton,
                    showWebPressableTransition && Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                    pressed && styles.saveButtonPressed,
                    hovered && Platform.OS === 'web' && styles.saveButtonHover,
                  ]}
                  onPress={onSave}
                  testID={testID ? `${testID}-display-name-save` : 'global-menu-modal-display-name-save'}
                >
                  <Text style={styles.saveButtonText}>{GLOBAL_MENU_COPY.SAVE}</Text>
                </Pressable>
                <Pressable
                  style={({ pressed, hovered }) => [
                    styles.cancelButtonInline,
                    showWebPressableTransition && Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                    pressed && styles.cancelButtonInlinePressed,
                    hovered && Platform.OS === 'web' && styles.cancelButtonInlineHover,
                  ]}
                  onPress={onCancelEdit}
                  testID={testID ? `${testID}-display-name-cancel` : 'global-menu-modal-display-name-cancel'}
                >
                  <Text style={styles.cancelButtonInlineText}>{GLOBAL_MENU_COPY.CANCEL}</Text>
                </Pressable>
              </View>
            </View>
          ) : (
            <>
              <View style={styles.displayNameSpacer} />
              <View style={styles.displayNameCenter}>
                <Text style={styles.userName}>{displayName}</Text>
              </View>
              <Pressable
                style={({ pressed, hovered }) => [
                  styles.editIconButton,
                  showWebPressableTransition && Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                  pressed && styles.editIconButtonPressed,
                  hovered && Platform.OS === 'web' && styles.editIconButtonHover,
                ]}
                onPress={onStartEdit}
                accessibilityLabel="Edit display name"
                testID={testID ? `${testID}-display-name-edit` : 'global-menu-modal-display-name-edit'}
              >
                <Pencil size={20} color={(theme.gray?.[600] ?? theme.gray?.[500]) ?? '#71717a'} />
              </Pressable>
            </>
          )}
        </View>

        {!isEditingDisplayName && usernameHandle ? (
          <Text style={styles.userHandle}>{usernameHandle}</Text>
        ) : null}

        {!isEditingDisplayName && isImpersonatingSession ? (
          <View style={styles.impersonationStatusWrap}>
            <Text style={styles.impersonationStatusText} numberOfLines={2}>
              {GLOBAL_MENU_IMPERSONATION.VIEWING_AS_PREFIX}
              {impersonationTargetLabel ? ` ${impersonationTargetLabel}` : ''}
            </Text>
            <Pressable
              accessibilityRole="button"
              onPress={onExitImpersonation}
              style={({ pressed }) => [
                styles.impersonationExitBtn,
                showWebPressableTransition && Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.impersonationExitBtnPressed,
              ]}
            >
              <Text style={styles.impersonationExitBtnText}>
                {GLOBAL_MENU_IMPERSONATION.EXIT_IMPERSONATION}
              </Text>
            </Pressable>
          </View>
        ) : null}

        {!isEditingDisplayName && isGuest ? (
          <View style={styles.guestUpgradeWrap}>
            <Text style={styles.guestUpgradeText} numberOfLines={2}>
              {GLOBAL_MENU_GUEST_UPGRADE.STATUS}
            </Text>
            <Pressable
              accessibilityRole="button"
              onPress={onUpgradeAccount}
              style={({ pressed }) => [
                styles.guestUpgradeBtn,
                showWebPressableTransition && Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.guestUpgradeBtnPressed,
              ]}
              testID={testID ? `${testID}-guest-upgrade` : 'global-menu-modal-guest-upgrade'}
            >
              <Text style={styles.guestUpgradeBtnText}>
                {GLOBAL_MENU_GUEST_UPGRADE.CTA}
              </Text>
            </Pressable>
          </View>
        ) : null}

        {!isEditingDisplayName && (
          <Pressable
            style={({ pressed, hovered }) => [
              styles.dailyRewardButton,
              showWebPressableTransition && Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.dailyRewardButtonPressed,
              hovered && Platform.OS === 'web' && styles.dailyRewardButtonHover,
            ]}
            onPress={onOpenDailyReward}
            accessibilityRole="button"
            accessibilityLabel="Collect daily coins"
            testID={testID ? `${testID}-daily-reward` : 'global-menu-modal-daily-reward'}
          >
            <Image source={coinIconSource} style={styles.menuItemCoinIconSmall} alt={WEB_IMAGE_ALT_DECORATIVE} />
            <Text style={styles.dailyRewardButtonText}>{coins.toLocaleString()}</Text>
          </Pressable>
        )}
      </View>
    </View>
  );
};
